import React, { useState } from 'react';
import { Box, Card, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import { IEvent } from '../../types/global/helper';
import { CheckCircleOutline, ExpandMore } from '@mui/icons-material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EventFeeBox } from './EventFeeBox';
import { EventList } from './EventList';
import { isEmpty } from '../../utils/lodash';
import CustomButton from '../Button/CustomButton';
import { LinkBox } from '../LinkBox';
import { generateShareLink } from '../../utils/AppUtils';
import { useNavigate } from 'react-router-dom';

export interface IEventSuggestion {
    event: IEvent;
    disableSuggestion?: boolean;
    similarEvents?: IEvent[];
    hideDetails?: boolean;
}

const EventRightBox: React.FC<IEventSuggestion> = ({ event, disableSuggestion, similarEvents, hideDetails }) => {
    const { themeStyle, device } = useThemeContext();
    const [linkBoxExpanded, setLinkBoxExpanded] = useState(false);

    const navigate = useNavigate();

    const toggleLinkBox = () => {
        setLinkBoxExpanded(!linkBoxExpanded);
    };

    return (
        <React.Fragment>
            <Stack direction="column">
                <Card
                    sx={{
                        width: '100%',
                        minWidth: '350px',
                        borderRadius: 2,
                        overflow: 'visible',
                        borderTop: `4px solid ${theme[themeStyle].highlight}`,
                        mb: 2,
                        p: 2,
                        position: 'relative',
                        backgroundColor: theme[themeStyle].secondaryBg,
                    }}>
                    <IconButton
                        sx={{
                            position: 'absolute',
                            top: 0,
                            right: 20,
                            borderRadius: 0,
                            borderBottomLeftRadius: '50%',
                            borderBottomRightRadius: '50%',
                            p: 0.5,
                            backgroundColor: theme[themeStyle].highlight,
                        }}>
                        <CheckCircleOutline sx={{ color: theme[themeStyle].true_white, fontSize: 24 }} />
                    </IconButton>
                    <EventFeeBox event={event} />
                    {!device.isDesktop && (
                        <>
                            <Box sx={{ mt: 2 }}>
                                <CustomButton
                                    label="Get shareable link"
                                    onClick={toggleLinkBox}
                                    buttonProps={{ variant: 'text' }}
                                    customStyle={{
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px 16px',
                                    }}
                                    withIcon
                                    customIcon={
                                        <ExpandMore
                                            sx={{
                                                transform: linkBoxExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.3s ease',
                                                color: theme[themeStyle].tag_white,
                                            }}
                                        />
                                    }
                                />
                                <Collapse in={linkBoxExpanded} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1 }}>
                                        <LinkBox link={generateShareLink('event', event, true)} />
                                    </Box>
                                </Collapse>
                            </Box>
                            {!hideDetails && (
                                <CustomButton
                                    label="View more details"
                                    onClick={() => navigate(`/event/${event.slug}`)}
                                    customStyle={{ marginTop: '10px', width: '100%' }}
                                    sx={{
                                        '&:hover': {
                                            WebkitTextFillColor: theme[themeStyle].primary_orange,
                                        },
                                    }}
                                />
                            )}
                        </>
                    )}
                </Card>
                {!disableSuggestion && (
                    <Card
                        sx={{
                            width: '100%',
                            borderRadius: 2,
                            overflow: 'visible',
                            p: 0,
                            minHeight: 'fit-content',
                            backgroundColor: theme[themeStyle].secondaryBg,
                        }}>
                        {similarEvents && !isEmpty(similarEvents) && (
                            <Box borderBottom={1}>
                                <Box px={2} py={1}>
                                    <Typography
                                        style={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 19,
                                            color: theme[themeStyle].tag_white,
                                        }}>
                                        Similar Events
                                    </Typography>
                                </Box>
                                <EventList events={similarEvents!} />
                            </Box>
                        )}
                    </Card>
                )}

                {device.isDesktop && (
                    <>
                        <LinkBox link={generateShareLink('event', event, true)} sx={{ mt: 2 }} />
                        {!hideDetails && (
                            <CustomButton
                                label="View more details"
                                onClick={() => navigate(`/event/${event.slug}`)}
                                customStyle={{ marginTop: '10px' }}
                                sx={{
                                    '&:hover': {
                                        WebkitTextFillColor: theme[themeStyle].primary_orange,
                                    },
                                }}
                            />
                        )}
                    </>
                )}
            </Stack>
        </React.Fragment>
    );
};

export { EventRightBox };
